import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import QuratorLayout from '../components/qurator/QuratorLayout';
import API from '../config/api';
import './EditDraft.css';

// Note: This is a large component. AWS S3 operations will be handled via backend API for security.
// PDF.js is loaded via CDN in index.html

const EditDraft = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const protocolId = searchParams.get('editid') || searchParams.get('protocol_id');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [protocol, setProtocol] = useState(null);
  const [steps, setSteps] = useState([]);
  const [reagents, setReagents] = useState([]);
  const [favorite, setFavorite] = useState(false);
  
  // File management
  const [stepFiles, setStepFiles] = useState({}); // { stepIndex: [files] }
  const [globalFiles, setGlobalFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({}); // { fileName: progress }
  const [aiProcessingProgress, setAiProcessingProgress] = useState({}); // { fileName: progress }
  const [showUploadSuccess, setShowUploadSuccess] = useState(false);
  const [processingResults, setProcessingResults] = useState([]); // Store file processing results
  const [showIrrelevantFileModal, setShowIrrelevantFileModal] = useState(false);
  const [irrelevantFile, setIrrelevantFile] = useState(null);
  const [showAiProcessedModal, setShowAiProcessedModal] = useState(false);
  
  // Action steps (per-step JSON in S3)
  const [showActionStepsModal, setShowActionStepsModal] = useState(false);
  const [actionStepsByStep, setActionStepsByStep] = useState({}); // { stepIndex: [actionStepObjects] }
  const [loadingActionSteps, setLoadingActionSteps] = useState(false);
  const [savingActionSteps, setSavingActionSteps] = useState(false);
  const [actionStepsError, setActionStepsError] = useState('');
  
  // Modals
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showGlobalUploadModal, setShowGlobalUploadModal] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(null);
  const [isGlobalUpload, setIsGlobalUpload] = useState(false);
  
  const fileInputRef = useRef(null);
  const globalFileInputRef = useRef(null);

  const fetchProtocol = useCallback(async () => {
    try {
      setLoading(true);
      const response = await API.get(`/qurator/protocols/${protocolId}`);
      
      if (response.data.success) {
        const data = response.data.protocol;
        setProtocol(data);
        setFavorite(data.favorite === '1' || data.favorite === 1);
        
        // Use ordered steps from backend (ordered by step_number) or fallback to parsing string
        const stepArray = data.orderedSteps && data.orderedSteps.length > 0
          ? data.orderedSteps.filter(s => s && s.trim())
          : (data.step_of_protocol ? data.step_of_protocol.split('`').filter(s => s.trim()) : []);
        setSteps(stepArray);
        
        // Load reagents
        if (response.data.reagents) {
          setReagents(response.data.reagents.map(r => ({
            name: r.reagent_name || '',
            vendor: r.reagent_vendor || '',
            lot: r.reagent_lot || '',
            serial: r.reagent_serial || ''
          })));
        }
        
        // Files and educational content will be loaded in a separate useEffect after steps are set
      }
    } catch (error) {
      console.error('Error fetching protocol:', error);
    } finally {
      setLoading(false);
    }
  }, [protocolId]);

  useEffect(() => {
    if (protocolId) {
      fetchProtocol();
    }
  }, [protocolId, fetchProtocol]);

  useEffect(() => {
    if (protocol && protocol.prt_id && steps.length > 0) {
      loadStepFiles();
      loadGlobalFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protocol?.prt_id, steps.length]);

  const loadStepFiles = async () => {
    try {
      const stepFilesMap = {};
      
      // Load files for each step
      for (let i = 0; i < steps.length; i++) {
        try {
          const response = await API.get(`/qurator/protocols/${protocolId}/files?step_index=${i}`);
          if (response.data.success) {
            stepFilesMap[i] = response.data.files || [];
          }
        } catch (error) {
          console.error(`Error loading files for step ${i}:`, error);
          stepFilesMap[i] = [];
        }
      }
      
      setStepFiles(stepFilesMap);
    } catch (error) {
      console.error('Error loading step files:', error);
      setStepFiles({});
    }
  };

  const loadGlobalFiles = async () => {
    try {
      const response = await API.get(`/qurator/protocols/${protocolId}/files/global`);
      if (response.data.success) {
        setGlobalFiles(response.data.files || []);
      } else {
        setGlobalFiles([]);
      }
    } catch (error) {
      console.error('Error loading global files:', error);
      setGlobalFiles([]);
    }
  };

  const loadActionStepsForStep = async (stepIndex) => {
    if (!protocolId || stepIndex === null || stepIndex === undefined) return;
    
    setLoadingActionSteps(true);
    setActionStepsError('');
    
    try {
      const stepNumber = stepIndex + 1;
      const response = await API.get(`/qurator/protocols/${protocolId}/action-steps`, {
        params: { step_number: stepNumber }
      });
      
      if (response.data.success) {
        setActionStepsByStep(prev => ({
          ...prev,
          [stepIndex]: Array.isArray(response.data.action_steps) ? response.data.action_steps : []
        }));
      } else {
        throw new Error(response.data.message || 'Failed to load action steps');
      }
    } catch (error) {
      console.error('Error loading action steps:', error);
      setActionStepsError(error.response?.data?.message || error.message || 'Failed to load action steps');
    } finally {
      setLoadingActionSteps(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!protocol) return;
    
    setSaving(true);
    
    try {
      const validSteps = steps.filter(s => s && s.trim());
      const validReagents = reagents.filter(r => r.name && r.name.trim());
      
      const response = await API.put(`/qurator/protocols/${protocolId}`, {
        draft_title: protocol.draft_title,
        protocol_description: protocol.prt_description,
        steps: validSteps,
        reagents: validReagents.map(r => r.name),
        reagent_vendor: reagents.map(r => r.vendor || ''),
        reagent_lot: reagents.map(r => r.lot || ''),
        reagent_serial: reagents.map(r => r.serial || ''),
        favorite: favorite ? '1' : '0'
      });
      
      if (response.data.success) {
        alert('Experiment updated successfully!');
        window.location.reload();
      } else {
        throw new Error(response.data.message || 'Update failed');
      }
    } catch (error) {
      console.error('Error updating protocol:', error);
      alert('Failed to update experiment. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const addStep = () => {
    setSteps([...steps, '']);
  };

  const updateStep = (index, value) => {
    const newSteps = [...steps];
    newSteps[index] = value;
    setSteps(newSteps);
  };

  const deleteStep = async (index) => {
    if (!window.confirm('Are you sure you want to delete this step and all associated files?')) {
      return;
    }
    
    try {
      // Delete files for this step
      const stepFilesList = stepFiles[index] || [];
      for (const file of stepFilesList) {
        try {
          await API.delete(`/qurator/protocols/${protocolId}/files`, {
            data: { file_key: file.Key }
          });
        } catch (error) {
          console.error(`Error deleting file ${file.Key}:`, error);
        }
      }
      
      // Remove from state
      const newSteps = steps.filter((_, i) => i !== index);
      setSteps(newSteps);
      
      // Update step files state
      const newStepFiles = { ...stepFiles };
      delete newStepFiles[index];
      // Reindex remaining steps
      const reindexedFiles = {};
      Object.keys(newStepFiles).forEach(oldIndex => {
        const oldIdx = parseInt(oldIndex);
        if (oldIdx > index) {
          reindexedFiles[oldIdx - 1] = newStepFiles[oldIndex];
        } else if (oldIdx < index) {
          reindexedFiles[oldIdx] = newStepFiles[oldIndex];
        }
      });
      setStepFiles(reindexedFiles);
    } catch (error) {
      console.error('Error deleting step:', error);
      alert('Failed to delete step');
    }
  };

  const addReagent = () => {
    setReagents([...reagents, { name: '', vendor: '', lot: '', serial: '' }]);
  };

  const updateReagent = (index, field, value) => {
    const newReagents = [...reagents];
    newReagents[index] = { ...newReagents[index], [field]: value };
    setReagents(newReagents);
  };

  const deleteReagent = (index) => {
    if (window.confirm('Are you sure you want to delete this reagent?')) {
      setReagents(reagents.filter((_, i) => i !== index));
    }
  };

  const openUploadModal = (stepIndex) => {
    setCurrentStepIndex(stepIndex);
    setIsGlobalUpload(false);
    setShowUploadModal(true);
  };

  const openGlobalUploadModal = () => {
    setIsGlobalUpload(true);
    setShowGlobalUploadModal(true);
  };

  const convertPdfToImages = async (pdfFile, protocolId, stepIndex) => {
    const images = [];
    try {
      const pdfjs = window.pdfjsLib || window.pdfjs;
      if (!pdfjs) {
        throw new Error('PDF.js library not loaded');
      }

      if (pdfjs.GlobalWorkerOptions && !pdfjs.GlobalWorkerOptions.workerSrc) {
        pdfjs.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      }

      const arrayBuffer = await pdfFile.arrayBuffer();
      const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      const scale = 2.0;
      const fileName = pdfFile.name;
      const fileBase = fileName.substring(0, fileName.lastIndexOf('.'));

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale });

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({
          canvasContext: context,
          viewport: viewport
        }).promise;

        const blob = await new Promise(resolve => 
          canvas.toBlob(resolve, 'image/jpeg', 0.95)
        );

        const stepNum = isGlobalUpload ? 0 : stepIndex + 1;
        const imageFile = new File(
          [blob],
          `${protocolId}_${stepNum}_${fileBase}_pdf_${pageNum}.jpg`,
          { type: 'image/jpeg' }
        );
        images.push(imageFile);
      }

      return images;
    } catch (error) {
      console.error('PDF conversion error:', error);
      throw new Error('Failed to convert PDF: ' + error.message);
    }
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    setUploadProgress({});
    setAiProcessingProgress({});
    setProcessingResults([]);
    
    try {
      const filesToUpload = [];
      
      // Process PDFs - convert to images
      for (const file of Array.from(files)) {
        if (file.type === 'application/pdf') {
          try {
            const stepIndex = isGlobalUpload ? -1 : currentStepIndex;
            const images = await convertPdfToImages(file, protocolId, stepIndex);
            
            // Add original PDF
            filesToUpload.push(file);
            
            // Add converted images
            filesToUpload.push(...images);
          } catch (error) {
            console.error('Error converting PDF:', error);
            alert(`Failed to convert PDF ${file.name}. Uploading original PDF only.`);
            filesToUpload.push(file);
          }
        } else {
          filesToUpload.push(file);
        }
      }

      // Upload all files with progress tracking
      const formData = new FormData();
      const progressMap = {};
      const aiProgressMap = {};
      const originalFiles = Array.from(files);
      
      filesToUpload.forEach(file => {
        formData.append('files', file);
        progressMap[file.name] = 0;
        // Initialize AI progress for original files only (not converted images)
        if (originalFiles.some(f => f.name === file.name)) {
          aiProgressMap[file.name] = 0;
        }
      });
      formData.append('step_index', isGlobalUpload ? -1 : currentStepIndex);
      formData.append('is_global', isGlobalUpload ? '1' : '0');

      setUploadProgress(progressMap);
      if (isGlobalUpload) {
        setAiProcessingProgress(aiProgressMap);
      }

      // Simulate AI processing progress (can run in background)
      let aiProgressInterval = null;
      if (isGlobalUpload) {
        aiProgressInterval = setInterval(() => {
          setAiProcessingProgress(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(key => {
              if (updated[key] < 90) {
                updated[key] = Math.min(updated[key] + 10, 90);
              }
            });
            return updated;
          });
        }, 500);
      }

      const response = await API.post(`/qurator/protocols/${protocolId}/files/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            // Update progress for all files (simplified - in real app, track per file)
            const updatedProgress = {};
            filesToUpload.forEach(file => {
              updatedProgress[file.name] = percentCompleted;
            });
            setUploadProgress(updatedProgress);
          }
        }
      });

      // Clear AI progress interval
      if (aiProgressInterval) {
        clearInterval(aiProgressInterval);
      }

      if (response.data.success) {
        // Complete upload progress
        setUploadProgress({});
        
        // Complete AI processing progress
        if (isGlobalUpload && response.data.file_processing_results) {
          const finalAiProgress = {};
          response.data.file_processing_results.forEach(result => {
            finalAiProgress[result.filename] = 100;
          });
          setAiProcessingProgress(finalAiProgress);
          
          // Store processing results
          setProcessingResults(response.data.file_processing_results);
          
          // Check for irrelevant files
          const irrelevantFiles = response.data.file_processing_results.filter(
            result => !result.is_relevant && result.processed
          );
          const relevantFiles = response.data.file_processing_results.filter(
            result => result.is_relevant && result.processed
          );
          
          // Store irrelevant files for processing
          if (irrelevantFiles.length > 0) {
            // Process first irrelevant file
            setIrrelevantFile(irrelevantFiles[0]);
            setShowIrrelevantFileModal(true);
          } else if (relevantFiles.length > 0) {
            // Show success for relevant files if no irrelevant files
            setShowAiProcessedModal(true);
            setTimeout(() => {
              setShowAiProcessedModal(false);
            }, 3000);
          }
        } else {
          setShowUploadSuccess(true);
          setTimeout(() => {
            setShowUploadSuccess(false);
          }, 3000);
        }
        
        // Reload files
        if (isGlobalUpload) {
          await loadGlobalFiles();
        } else {
          await loadStepFiles();
        }
        
        setShowUploadModal(false);
        setShowGlobalUploadModal(false);
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Failed to upload files. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress({});
      setAiProcessingProgress({});
    }
  };

  const handleIrrelevantFileDecision = async (keepFile) => {
    if (!irrelevantFile) return;
    
    const currentFile = irrelevantFile;
    
    if (!keepFile) {
      // Delete file from S3
      try {
        const response = await API.delete(`/qurator/protocols/${protocolId}/files`, {
          data: { file_key: currentFile.s3_key }
        });
        
        if (response.data.success) {
          // Remove from processing results
          setProcessingResults(prev => prev.filter(r => r.filename !== currentFile.filename));
        }
      } catch (error) {
        console.error('Error deleting file:', error);
        alert('Failed to delete file. Please try again.');
      }
    }
    
    setShowIrrelevantFileModal(false);
    
    // Check if there are more irrelevant files to process
    const remainingIrrelevant = processingResults.filter(
      r => !r.is_relevant && r.processed && r.filename !== currentFile.filename
    );
    const relevantFiles = processingResults.filter(
      r => r.is_relevant && r.processed
    );
    
    if (remainingIrrelevant.length > 0) {
      // Process next irrelevant file
      setIrrelevantFile(remainingIrrelevant[0]);
      setShowIrrelevantFileModal(true);
    } else if (relevantFiles.length > 0) {
      // Show success for relevant files
      setShowAiProcessedModal(true);
      setTimeout(() => {
        setShowAiProcessedModal(false);
      }, 3000);
    }
    
    setIrrelevantFile(null);
  };

  const deleteFile = async (fileKey, stepIndex) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }
    
    try {
      const response = await API.delete(`/qurator/protocols/${protocolId}/files`, {
        data: { file_key: fileKey }
      });
      
      if (response.data.success) {
        // Reload files
        if (stepIndex === -1) {
          await loadGlobalFiles();
        } else {
          await loadStepFiles();
        }
      } else {
        throw new Error(response.data.message || 'Delete failed');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Failed to delete file');
    }
  };

  const updateActionStepTitle = (stepIndex, actionStepIndex, newTitle) => {
    setActionStepsByStep(prev => {
      const existing = prev[stepIndex] || [];
      const updated = existing.map((item, idx) =>
        idx === actionStepIndex ? { ...item, title: newTitle } : item
      );
      return { ...prev, [stepIndex]: updated };
    });
  };

  const handleSaveActionSteps = async (stepIndex) => {
    if (stepIndex === null || stepIndex === undefined) {
      return;
    }
    
    const actionSteps = actionStepsByStep[stepIndex] || [];
    
    setSavingActionSteps(true);
    setActionStepsError('');
    
    try {
      const response = await API.put(`/qurator/protocols/${protocolId}/action-steps`, {
        step_number: stepIndex + 1,
        action_steps: actionSteps
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to save action steps');
      }
      
      alert('Action steps updated successfully');
    } catch (error) {
      console.error('Error saving action steps:', error);
      setActionStepsError(error.response?.data?.message || error.message || 'Failed to save action steps');
    } finally {
      setSavingActionSteps(false);
    }
  };

  if (loading) {
    return (
      <QuratorLayout>
        <div className="protocol-edit-container">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading experiment...</p>
          </div>
        </div>
      </QuratorLayout>
    );
  }

  if (!protocol) {
    return (
      <QuratorLayout>
        <div className="protocol-edit-container">
          <div className="alert alert-danger">Experiment not found</div>
        </div>
      </QuratorLayout>
    );
  }

  return (
    <QuratorLayout>
      <div className="protocol-edit-container">
        <div className="protocol-header">
          <h4>Edit Experiment</h4>
          <p className="mb-0">Update your experiment details</p>
        </div>

        <form id="protocolForm" onSubmit={handleSubmit}>
          {/* Basic Information */}
          <div className="protocol-section">
            <div className="section-title">
              <i className="bi bi-info-circle"></i>
              <h5>Basic Information</h5>
            </div>
            <div className="mb-3">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={favorite}
                  onChange={(e) => setFavorite(e.target.checked)}
                />
                <label className="form-check-label">Make Favorite</label>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Experiment Title</label>
              <input
                type="text"
                className="form-control"
                value={protocol.draft_title || ''}
                onChange={(e) => setProtocol({ ...protocol, draft_title: e.target.value })}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                rows="3"
                value={protocol.prt_description || ''}
                onChange={(e) => setProtocol({ ...protocol, prt_description: e.target.value })}
                required
              />
            </div>
          </div>

          {/* Global Files Section */}
          <div className="global-files-section">
            <div className="section-title">
              <i className="bi bi-cloud-upload"></i>
              <h5>Global Files for Entire Experiment</h5>
            </div>
            <p className="mb-3">Upload files that apply to the entire experiment (PDFs, images, videos, etc.)</p>
            <button
              type="button"
              className="step-button global-upload-btn"
              onClick={openGlobalUploadModal}
            >
              <i className="bi bi-cloud-upload"></i>
              Upload Global Files
            </button>
            
            {globalFiles.length > 0 && (
              <div className="files-section mt-3">
                <button
                  type="button"
                  className="toggle-files-btn"
                  onClick={() => {
                    const list = document.getElementById('globalFileList');
                    const btn = document.getElementById('global-files-toggle');
                    if (list) list.classList.toggle('show');
                    if (btn) btn.classList.toggle('expanded');
                  }}
                  id="global-files-toggle"
                >
                  <i className="bi bi-chevron-down"></i>
                  <span>Global Files</span>
                  <span className="file-count-badge">{globalFiles.length} files</span>
                </button>
                
                <div id="globalFileList" className="educational-content-area">
                  <div className="content-panel">
                    <div className="panel-header">
                      <i className="bi bi-folder"></i>
                      Global Files
                    </div>
                    <div>
                      {globalFiles.map((file, idx) => (
                        <div key={idx} className="file-item">
                          <div className="file-icon">
                            <i className={`bi ${getFileIcon(file.Name)}`}></i>
                          </div>
                          <div className="file-details">
                            <p className="file-name">{file.Name}</p>
                            <p className="file-info">
                              {formatFileSize(file.Size)} • {new Date(file.LastModified).toLocaleDateString()}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="delete-file"
                            onClick={() => deleteFile(file.Key, -1)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Steps Section */}
          <div className="protocol-section">
            <div className="section-title">
              <i className="bi bi-list-ol"></i>
              <h5>Experiment Steps</h5>
            </div>
            <div id="stepsContainer">
              {steps.map((step, index) => {
                const hasFiles = stepFiles[index]?.length > 0;
                const showToggle = true;
                
                return (
                <div key={index} className="step-item">
                  <div className="step-content">
                    <textarea
                      className="form-control"
                      rows="2"
                      value={step}
                      onChange={(e) => updateStep(index, e.target.value)}
                      required
                    />
                    <div className="step-actions">
                      <button
                        type="button"
                        className="step-button upload-btn"
                        onClick={() => openUploadModal(index)}
                      >
                        <i className="bi bi-paperclip"></i>
                        <span className="d-none d-md-inline">Upload</span>
                      </button>
                      {/* Action steps are shown in the dropdown area below */}
                      <button
                        type="button"
                        className="step-button delete-step-btn"
                        onClick={() => deleteStep(index)}
                      >
                        <i className="bi bi-trash"></i>
                        <span className="d-none d-md-inline">Delete</span>
                      </button>
                    </div>
                  </div>
                  
                  {/* Toggle Files/Content Button - At bottom of step context */}
                  {showToggle && (
                    <div className="toggle-files-container">
                      <button
                        type="button"
                        className="toggle-files-btn"
                        onClick={(e) => {
                          const list = document.getElementById(`fileList-${index}`);
                          const btn = e.currentTarget;
                          if (list) list.classList.toggle('show');
                          if (btn) btn.classList.toggle('expanded');
                        }}
                      >
                        <i className="bi bi-chevron-down"></i>
                        <span>View Content & Files</span>
                        {hasFiles && (
                          <span className="file-count-badge">{stepFiles[index].length} file{stepFiles[index].length !== 1 ? 's' : ''}</span>
                        )}
                      </button>
                    </div>
                  )}
                  
                  {/* Files and Action Steps */}
                  <div className="files-section" id={`files-section-${index}`}>
                    <div id={`fileList-${index}`} className="educational-content-area">
                      <div className="content-split">
                        {/* Files Panel */}
                        <div className="content-panel">
                          <div className="panel-header">
                            <i className="bi bi-folder"></i>
                            Attached Files
                          </div>
                          <div>
                            {stepFiles[index]?.length > 0 ? (
                              stepFiles[index].map((file, idx) => (
                                <div key={idx} className="file-item">
                                  <div className="file-icon">
                                    <i className={`bi ${getFileIcon(file.Name)}`}></i>
                                  </div>
                                  <div className="file-details">
                                    <p className="file-name">{file.Name}</p>
                                    <p className="file-info">
                                      {formatFileSize(file.Size)} • {new Date(file.LastModified).toLocaleDateString()}
                                    </p>
                                  </div>
                                  <button
                                    type="button"
                                    className="delete-file"
                                    onClick={() => deleteFile(file.Key, index)}
                                  >
                                    <i className="bi bi-trash"></i>
                                  </button>
                                </div>
                              ))
                            ) : (
                              <div className="text-center py-3 text-muted">
                                <i className="bi bi-folder2-open" style={{ fontSize: '2rem', opacity: 0.3 }}></i>
                                <p className="mt-2 mb-0">No files uploaded</p>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Action Steps Panel */}
                        <div className="content-panel">
                          <div className="panel-header">
                            <i className="bi bi-list-task"></i>
                            Action Steps
                          </div>
                          <p className="text-muted" style={{ fontSize: '0.8rem' }}>
                            These titles come from the step&apos;s JSON file. Changes here update that file in S3.
                          </p>
                          {loadingActionSteps && !actionStepsByStep[index] && (
                            <div className="text-center py-3">
                              <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                              <p className="mt-2 mb-0">Loading action steps...</p>
                            </div>
                          )}
                          {!loadingActionSteps && (!actionStepsByStep[index] || (actionStepsByStep[index] || []).length === 0) && (
                            <div className="text-center py-3 text-muted">
                              <i className="bi bi-info-circle" style={{ fontSize: '1.5rem', opacity: 0.4 }}></i>
                              <p className="mt-2 mb-0">No action steps found for this step&apos;s JSON.</p>
                            </div>
                          )}
                          {(actionStepsByStep[index] || []).length > 0 && (
                            <div className="list-group">
                              {actionStepsByStep[index].map((stepItem, idx) => (
                                <div
                                  key={stepItem.id || idx}
                                  className="list-group-item d-flex flex-column"
                                  style={{ gap: '0.4rem' }}
                                >
                                  <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                                    <span
                                      className="badge bg-secondary me-2"
                                      style={{ minWidth: '2rem', textAlign: 'center' }}
                                    >
                                      {idx + 1}
                                    </span>
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={stepItem.title || ''}
                                      onChange={(e) =>
                                        updateActionStepTitle(index, idx, e.target.value)
                                      }
                                      placeholder="Action step title"
                                    />
                                  </div>
                                  {stepItem.step_context && (
                                    <small className="text-muted" style={{ marginLeft: '2.4rem' }}>
                                      {stepItem.step_context}
                                    </small>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          {actionStepsError && (
                            <div className="alert alert-danger mt-3 mb-0">
                              <i className="bi bi-exclamation-triangle me-2"></i>
                              {actionStepsError}
                            </div>
                          )}
                          <div className="action-step-footer mt-3">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => navigate(`/human-validation?editid=${protocolId}&step=${index + 1}`)}
                            >
                              <i className="bi bi-person-check me-2"></i>
                              Human Validation
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={async () => {
                                if (!actionStepsByStep[index]) {
                                  await loadActionStepsForStep(index);
                                } else {
                                  await handleSaveActionSteps(index);
                                }
                              }}
                              disabled={savingActionSteps}
                            >
                              {actionStepsByStep[index] ? (savingActionSteps ? 'Saving...' : 'Save Changes') : 'Load Action Steps'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
            <button type="button" className="btn btn-outline-primary mt-3" onClick={addStep}>
              <i className="bi bi-plus-lg"></i> Add Step
            </button>
          </div>

          {/* Reagents Section */}
          <div className="protocol-section">
            <div className="section-title">
              <i className="bi bi-flask"></i>
              <h5>Reagents List</h5>
            </div>
            <div id="reagentsContainer">
              {reagents.map((reagent, index) => (
                <div key={index} className="step-item">
                  <div className="reagent-row">
                    <div className="reagent-main">
                      <input
                        type="text"
                        className="form-control"
                        value={reagent.name}
                        onChange={(e) => updateReagent(index, 'name', e.target.value)}
                        placeholder="Enter reagent name"
                        required
                      />
                    </div>
                    <div className="reagent-details">
                      <input
                        type="text"
                        className="form-control"
                        value={reagent.vendor}
                        onChange={(e) => updateReagent(index, 'vendor', e.target.value)}
                        placeholder="Vendor"
                      />
                      <input
                        type="text"
                        className="form-control"
                        value={reagent.lot}
                        onChange={(e) => updateReagent(index, 'lot', e.target.value)}
                        placeholder="Lot #"
                      />
                      <input
                        type="text"
                        className="form-control"
                        value={reagent.serial}
                        onChange={(e) => updateReagent(index, 'serial', e.target.value)}
                        placeholder="Serial #"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    className="delete-file"
                    onClick={() => deleteReagent(index)}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              ))}
            </div>
            <button type="button" className="btn btn-outline-primary mt-3" onClick={addReagent}>
              <i className="bi bi-plus-lg"></i> Add Reagent
            </button>
          </div>

          {/* Submit Button */}
          <div className="text-end mt-4">
            <button type="submit" className="btn btn-primary px-4" disabled={saving}>
              <i className="bi bi-check-lg me-2"></i>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>

        {/* File Upload Modal */}
        {showUploadModal && (
          <div className={`modal fade show`} style={{ display: 'block' }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Upload Files</h5>
                  <button type="button" className="btn-close" onClick={() => setShowUploadModal(false)}></button>
                </div>
                <div className="modal-body">
                  <div
                    className="file-upload-area"
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('drag-over'); }}
                    onDragLeave={(e) => e.currentTarget.classList.remove('drag-over')}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('drag-over');
                      handleFileUpload(e.dataTransfer.files);
                    }}
                  >
                    <i className="bi bi-cloud-upload upload-icon"></i>
                    <h6>Drag and drop files here</h6>
                    <p className="text-muted">or</p>
                    <input
                      type="file"
                      ref={fileInputRef}
                      multiple
                      className="d-none"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.mp4"
                      onChange={(e) => handleFileUpload(e.target.files)}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      Browse Files
                    </button>
                  </div>
                  {uploading && Object.keys(uploadProgress).length > 0 && (
                    <div className="upload-progress-container mt-3">
                      <h6 className="mb-2">Upload Progress</h6>
                      {Object.entries(uploadProgress).map(([fileName, progress]) => (
                        <div key={fileName} className="upload-progress-item mb-2">
                          <div className="upload-progress-header">
                            <span className="upload-file-name">{fileName}</span>
                            <span className="upload-progress-percent">{progress}%</span>
                          </div>
                          <div className="upload-progress-bar">
                            <div 
                              className="upload-progress-fill" 
                              style={{ width: `${progress}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {showUploadSuccess && (
                    <div className="upload-success-popup">
                      <i className="bi bi-check-circle-fill"></i>
                      <span>Files uploaded successfully!</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Global Upload Modal */}
        {showGlobalUploadModal && (
          <div className={`modal fade show`} style={{ display: 'block' }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Upload Global Files</h5>
                  <button type="button" className="btn-close" onClick={() => setShowGlobalUploadModal(false)}></button>
                </div>
                <div className="modal-body">
                  <div
                    className="file-upload-area"
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('drag-over'); }}
                    onDragLeave={(e) => e.currentTarget.classList.remove('drag-over')}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('drag-over');
                      handleFileUpload(e.dataTransfer.files);
                    }}
                  >
                    <i className="bi bi-cloud-upload upload-icon"></i>
                    <h6>Drag and drop global files here</h6>
                    <p className="text-muted">Files that apply to the entire experiment</p>
                    <input
                      type="file"
                      ref={globalFileInputRef}
                      multiple
                      className="d-none"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.mp4"
                      onChange={(e) => handleFileUpload(e.target.files)}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() => globalFileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      Browse Files
                    </button>
                  </div>
                  {uploading && Object.keys(uploadProgress).length > 0 && (
                    <div className="upload-progress-container mt-3">
                      <h6 className="mb-2">Upload Progress</h6>
                      {Object.entries(uploadProgress).map(([fileName, progress]) => (
                        <div key={fileName} className="upload-progress-item mb-2">
                          <div className="upload-progress-header">
                            <span className="upload-file-name">{fileName}</span>
                            <span className="upload-progress-percent">{progress}%</span>
                          </div>
                          <div className="upload-progress-bar">
                            <div 
                              className="upload-progress-fill" 
                              style={{ width: `${progress}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {isGlobalUpload && uploading && Object.keys(aiProcessingProgress).length > 0 && (
                    <div className="upload-progress-container mt-3">
                      <h6 className="mb-2">AI Processing Progress</h6>
                      {Object.entries(aiProcessingProgress).map(([fileName, progress]) => (
                        <div key={`ai-${fileName}`} className="upload-progress-item mb-2">
                          <div className="upload-progress-header">
                            <span className="upload-file-name">
                              <i className="bi bi-robot me-2"></i>
                              {fileName}
                            </span>
                            <span className="upload-progress-percent">{progress}%</span>
                          </div>
                          <div className="upload-progress-bar" style={{ backgroundColor: '#e3f2fd' }}>
                            <div 
                              className="upload-progress-fill" 
                              style={{ width: `${progress}%`, backgroundColor: '#2196f3' }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {showUploadSuccess && (
                    <div className="upload-success-popup">
                      <i className="bi bi-check-circle-fill"></i>
                      <span>Files uploaded successfully!</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Irrelevant File Modal */}
        {showIrrelevantFileModal && irrelevantFile && (
          <div className={`modal fade show`} style={{ display: 'block', zIndex: 1055 }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header bg-warning">
                  <h5 className="modal-title">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    File Not Relevant
                  </h5>
                </div>
                <div className="modal-body">
                  <p><strong>File:</strong> {irrelevantFile.filename}</p>
                  <p><strong>Reason:</strong> {irrelevantFile.relevance_reason || 'This file does not appear to be related to the experiment.'}</p>
                  <p className="text-muted">What would you like to do with this file?</p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => handleIrrelevantFileDecision(false)}
                  >
                    <i className="bi bi-trash me-2"></i>
                    Delete
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => handleIrrelevantFileDecision(true)}
                  >
                    <i className="bi bi-check-lg me-2"></i>
                    Keep Anyway
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* AI Processed Completed Modal */}
        {showAiProcessedModal && (
          <div className={`modal fade show`} style={{ display: 'block', zIndex: 1055 }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header bg-success text-white">
                  <h5 className="modal-title">
                    <i className="bi bi-check-circle-fill me-2"></i>
                    AI Processing Completed
                  </h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowAiProcessedModal(false)}></button>
                </div>
                <div className="modal-body">
                  <p>The file has been successfully processed by AI and knowledge has been extracted.</p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={() => setShowAiProcessedModal(false)}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </QuratorLayout>
  );
};

// Helper functions
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileIcon = (fileName) => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'pdf':
      return 'bi-file-pdf-fill';
    case 'jpg':
    case 'jpeg':
    case 'png':
      return 'bi-file-image-fill';
    case 'doc':
    case 'docx':
      return 'bi-file-word-fill';
    case 'mp4':
      return 'bi-file-play-fill';
    default:
      return 'bi-file-earmark-fill';
  }
};

export default EditDraft;
